import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Clock, Users, Check, ArrowRight, Plane, ChevronLeft, ChevronRight } from 'lucide-react';
import currency from 'currency.js';

const exchangeRates = {
  CHF: 1,
  USD: 1.1,
  EUR: 1.02,
  GBP: 0.9
};

export default function EmptyLegModal({ offer, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    currency: offer?.currency || 'EUR'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAddons, setShowAddons] = useState(false);
  const [addons, setAddons] = useState([]);
  const [departureDate, setDepartureDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Set departure date from offer on component mount
  useEffect(() => {
    if (offer?.departure_date) {
      setDepartureDate(new Date(offer.departure_date));
    }
  }, [offer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleAddon = (addon) => {
    setAddons((prev) =>
      prev.includes(addon) ? prev.filter((a) => a !== addon) : [...prev, addon]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const currencyCode = formData.currency;
      const rate = exchangeRates[currencyCode] || 1;
      const base = offer.price * rate;
      const tax = base * 0.081;
      const total = base + tax;

      // In a real application, you would send this data to your backend
      const emailText = `New Empty Leg Booking Request\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nOffer: ${offer.title}\nDate: ${departureDate?.toDateString()}\n\nPricing:\nSubtotal: ${currencyCode} ${currency(base).format()}\nTaxes: ${currencyCode} ${currency(tax).format()}\nTotal incl. tax: ${currencyCode} ${currency(total).format()}\n\nAdd-ons: ${addons.join(', ') || 'None'}\n\nMessage:\n${formData.message}`;

      console.log(emailText);
      
      // Simulate API call to submit booking
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
    } catch (err) {
      console.error('Submission error', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Generate calendar for current month
  const renderCalendar = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get the first day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();
    
    // Get the number of days in the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Create array for calendar days
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Create weeks array
    const weeks = [];
    let week = [];
    
    days.forEach((day, index) => {
      week.push(day);
      if ((index + 1) % 7 === 0 || index === days.length - 1) {
        weeks.push(week);
        week = [];
      }
    });
    
    return (
      <div className="calendar-dropdown bg-white rounded-lg shadow-xl border border-gray-200 p-4 absolute top-full left-0 z-10 w-80 mt-1">
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={goToPreviousMonth}
            className="p-1 rounded-full hover:bg-gray-200"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-lg font-medium">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button 
            onClick={goToNextMonth}
            className="p-1 rounded-full hover:bg-gray-200"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {weeks.flat().map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="h-10"></div>;
            }
            
            const isToday = date.toDateString() === today.toDateString();
            const isSelected = departureDate && date.toDateString() === departureDate.toDateString();
            const isPast = date < today;
            const isOfferDate = offer?.departure_date && date.toDateString() === new Date(offer.departure_date).toDateString();
            
            return (
              <button
                key={date.toDateString()}
                onClick={() => {
                  if (isOfferDate) {
                    setDepartureDate(date);
                    setShowCalendar(false);
                  }
                }}
                disabled={!isOfferDate || isPast}
                className={`h-10 w-full flex items-center justify-center rounded-full text-sm
                  ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                  ${isToday && !isSelected ? 'border border-black' : ''}
                  ${isSelected ? 'bg-black text-white' : ''}
                  ${isOfferDate && !isPast && !isSelected ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : ''}
                  ${!isOfferDate && !isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                `}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
        
        <div className="mt-4 text-center text-sm text-orange-600">
          <p>Only the highlighted date is available for this empty leg flight.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden mx-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/80 rounded-full text-gray-600 hover:text-gray-900 z-10"
          >
            <X size={24} />
          </button>

          <div className="relative w-full h-64">
            <img
              src={offer.image_url || 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80'}
              alt={offer.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-orange-500 text-white text-sm font-medium px-3 py-1 rounded-full">
              Empty Leg
            </div>
          </div>

          <div className="px-6 sm:px-8 pt-8 pb-10">
            <h1 className="text-3xl font-bold mb-3">{offer.title}</h1>
            
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-6 text-base">
              <div className="flex items-center text-gray-700">
                <div className="flex items-center mr-2 text-black font-medium">
                  <MapPin size={18} className="mr-1 text-gray-500" />
                  {offer.origin}
                </div>
                <ArrowRight size={16} className="text-gray-400 mx-1" />
                <div className="font-medium">{offer.destination}</div>
              </div>
              
              <div className="flex items-center text-gray-700 relative">
                <button 
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="flex items-center bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Calendar size={18} className="mr-2 text-gray-500" />
                  <span className="font-medium">{formatDate(offer.departure_date)}</span>
                </button>
                
                {showCalendar && renderCalendar()}
              </div>
              
              <div className="flex items-center text-gray-700">
                <Clock size={18} className="mr-2 text-gray-500" />
                <span className="font-medium">{offer.duration}</span>
              </div>
              
              <div className="flex items-center text-gray-700">
                <Users size={18} className="mr-2 text-gray-500" />
                <span className="font-medium">Up to {offer.capacity} passengers</span>
              </div>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg mb-8">
              <div className="flex items-center gap-2 text-orange-800">
                <Plane className="flex-shrink-0" size={20} />
                <p className="text-base font-medium">
                  This is an empty leg flight with a fixed departure date. The date cannot be changed.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">Flight Details</h3>
                <div className="bg-gray-50 p-6 rounded-xl space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base">Aircraft</span>
                    <span className="font-medium text-base">{offer.aircraft_model}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base">Category</span>
                    <span className="font-medium text-base">{offer.aircraft_category}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base">Departure</span>
                    <span className="font-medium text-base">{formatDate(offer.departure_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base">Flight Duration</span>
                    <span className="font-medium text-base">{offer.duration}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base">Capacity</span>
                    <span className="font-medium text-base">Up to {offer.capacity} passengers</span>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-4">Included Services</h3>
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-base">VIP terminal access</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-base">Onboard catering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-base">Dedicated flight attendant</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-base">Luggage allowance (15kg per person)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Booking Details</h3>
                
                <div className="mb-4">
                  <label className="block font-medium mb-2 text-base">Preferred Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-base"
                  >
                    <option value="CHF">CHF</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block font-medium mb-2 text-base flex items-center justify-between">
                    Select Additional Services
                    <button
                      type="button"
                      onClick={() => setShowAddons(!showAddons)}
                      className="ml-2 text-xl text-black"
                    >
                      {showAddons ? '-' : '+'}
                    </button>
                  </label>
                  {showAddons && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {['Concierge Service', 'Airport Pickup', 'Champagne On Board', 'Extra Baggage'].map((addon) => (
                        <label key={addon} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={addons.includes(addon)}
                            onChange={() => toggleAddon(addon)}
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="text-base">{addon}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-6 border border-gray-200 p-5 rounded-lg bg-gray-50">
                  {(() => {
                    const currencyCode = formData.currency;
                    const rate = exchangeRates[currencyCode] || 1;
                    const base = offer.price * rate;
                    const tax = base * 0.081;
                    const total = base + tax;
                    const regularPrice = base * 3; // Regular price is 3x the empty leg price
                    return (
                      <>
                        <div className="flex justify-between text-base mb-1">
                          <span className="text-gray-600">Base Price</span>
                          <span className="font-semibold">{currencyCode} {currency(base).format()}</span>
                        </div>
                        <div className="flex justify-between text-base mb-1">
                          <span className="text-gray-600">Regular Price</span>
                          <span className="line-through text-gray-500">{currencyCode} {currency(regularPrice).format()}</span>
                        </div>
                        <div className="flex justify-between text-base mb-1">
                          <span className="text-gray-600">Taxes (8.1%)</span>
                          <span>{currencyCode} {currency(tax).format()}</span>
                        </div>
                        <div className="border-t border-gray-300 my-2"></div>
                        <div className="flex justify-between font-semibold text-lg">
                          <span>Total</span>
                          <span>{currencyCode} {currency(total).format()}</span>
                        </div>
                        <div className="text-base text-green-600 font-medium mt-1 text-right">
                          Save up to 75% off regular price
                        </div>
                      </>
                    );
                  })()}
                </div>

                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      required 
                      placeholder="Full Name" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-base" 
                    />
                    <input 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      placeholder="Email Address" 
                      type="email" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-base" 
                    />
                    <div className="flex">
                      <div className="bg-gray-100 flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300">
                        <span className="text-gray-500 text-base">+</span>
                      </div>
                      <input 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        required 
                        placeholder="Phone Number" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-black focus:border-transparent text-base" 
                      />
                    </div>
                    <textarea 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      placeholder="Additional Requests" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none text-base" 
                      rows={3}
                    ></textarea>
                    <button 
                      disabled={isSubmitting} 
                      type="submit" 
                      className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors text-base font-medium"
                    >
                      {isSubmitting ? 'Sending…' : 'Request Booking'}
                    </button>
                  </form>
                ) : (
                  <div className="bg-green-100 text-green-800 p-5 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={24} className="text-green-600" />
                      <h3 className="font-bold text-lg">Booking Request Sent!</h3>
                    </div>
                    <p className="text-base">Thank you for your booking request. Our team will contact you shortly to confirm your reservation.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 text-center text-base text-gray-500">
              Questions? Email <a href="mailto:bookings@privatecharterx.com" className="underline">bookings@privatecharterx.com</a> or call <a href="tel:+41447978853" className="underline">+41 (0) 44 797 88 53</a><br />
              Languages: Deutsch, English, Français, Italiano, Español
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}